export enum RoutePath {
  Dashboard = "/",
  Login = "/",
  VerifyEmail = "/verify-email",
  Booking = "/booking",
  BookingPayment = "/booking/payment",
  ManageBookings = "/bookings",
  BookingDetail = "/bookings/:orderId",
}

export const urls = {
  privacy: "https://health4travel.com/privacy-policy/",
  termsOfUse: "https://health4travel.com/terms-and-condition/",
  returnsAndExchanges: "https://health4travel.com/privacy-policy/",
  contactUs: process.env.REACT_APP_CONTACT_URL ?? "",
  sendFeedback: process.env.REACT_APP_CONTACT_URL ?? "",
  facebook: "https://www.facebook.com/health4travel",
  twitter: "https://x.com/AirportVax",
  instagram: "https://www.instagram.com/health4travel",
  linkedin: "https://www.linkedin.com/company/health4travel/",
  bookingSuccess: ({
    order_id,
    code,
    telehealth_order = false,
    free = false,
  }: {
    order_id: string | number;
    code: string;
    telehealth_order?: boolean;
    free?: boolean;
  }) =>
    `/?paymentSuccess=true&order_id=${order_id}&code=${code}&telehealth_order=${telehealth_order}&free=${free}`,
} as const;

export type Country = {
  dial: `+${string}`;
  iso2: string;
  name: string;
  nationalMaxDigits: number;
  nationalFormat?: { re: RegExp; fmt: string };
};

/** Matches booking form defaults when no dial is present yet. */
export const defaultPhoneDial = "+31" as const;

export function flagSrc(iso2: string) {
  return `https://flagcdn.com/${iso2.toLowerCase()}.svg`;
}

export const countries: ReadonlyArray<Country> = [
  { dial: "+852", iso2: "hk", name: "Hong Kong", nationalMaxDigits: 8 },
  { dial: "+880", iso2: "bd", name: "Bangladesh", nationalMaxDigits: 10 },
  { dial: "+886", iso2: "tw", name: "Taiwan", nationalMaxDigits: 9 },
  { dial: "+966", iso2: "sa", name: "Saudi Arabia", nationalMaxDigits: 9 },
  { dial: "+968", iso2: "om", name: "Oman", nationalMaxDigits: 8 },
  { dial: "+970", iso2: "ps", name: "Palestine", nationalMaxDigits: 9 },
  {
    dial: "+971",
    iso2: "ae",
    name: "United Arab Emirates",
    nationalMaxDigits: 9,
  },
  { dial: "+973", iso2: "bh", name: "Bahrain", nationalMaxDigits: 8 },
  { dial: "+974", iso2: "qa", name: "Qatar", nationalMaxDigits: 8 },
  { dial: "+965", iso2: "kw", name: "Kuwait", nationalMaxDigits: 8 },
  { dial: "+233", iso2: "gh", name: "Ghana", nationalMaxDigits: 9 },
  { dial: "+234", iso2: "ng", name: "Nigeria", nationalMaxDigits: 10 },
  { dial: "+254", iso2: "ke", name: "Kenya", nationalMaxDigits: 9 },
  { dial: "+255", iso2: "tz", name: "Tanzania", nationalMaxDigits: 9 },
  { dial: "+256", iso2: "ug", name: "Uganda", nationalMaxDigits: 9 },
  { dial: "+351", iso2: "pt", name: "Portugal", nationalMaxDigits: 9 },
  { dial: "+352", iso2: "lu", name: "Luxembourg", nationalMaxDigits: 9 },
  { dial: "+353", iso2: "ie", name: "Ireland", nationalMaxDigits: 9 },
  { dial: "+354", iso2: "is", name: "Iceland", nationalMaxDigits: 7 },
  { dial: "+356", iso2: "mt", name: "Malta", nationalMaxDigits: 8 },
  { dial: "+357", iso2: "cy", name: "Cyprus", nationalMaxDigits: 8 },
  {
    dial: "+358",
    iso2: "fi",
    name: "Finland",
    nationalMaxDigits: 9,
    nationalFormat: { re: /^(\d{2})(\d{3})(\d{4})$/, fmt: "$1$2$3" },
  },
  { dial: "+359", iso2: "bg", name: "Bulgaria", nationalMaxDigits: 9 },
  { dial: "+370", iso2: "lt", name: "Lithuania", nationalMaxDigits: 8 },
  { dial: "+371", iso2: "lv", name: "Latvia", nationalMaxDigits: 8 },
  { dial: "+372", iso2: "ee", name: "Estonia", nationalMaxDigits: 8 },
  { dial: "+380", iso2: "ua", name: "Ukraine", nationalMaxDigits: 9 },
  { dial: "+381", iso2: "rs", name: "Serbia", nationalMaxDigits: 9 },
  { dial: "+382", iso2: "me", name: "Montenegro", nationalMaxDigits: 8 },
  { dial: "+385", iso2: "hr", name: "Croatia", nationalMaxDigits: 9 },
  { dial: "+386", iso2: "si", name: "Slovenia", nationalMaxDigits: 8 },
  {
    dial: "+387",
    iso2: "ba",
    name: "Bosnia and Herzegovina",
    nationalMaxDigits: 8,
  },
  { dial: "+389", iso2: "mk", name: "North Macedonia", nationalMaxDigits: 8 },
  {
    dial: "+420",
    iso2: "cz",
    name: "Czechia",
    nationalMaxDigits: 9,
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  {
    dial: "+421",
    iso2: "sk",
    name: "Slovakia",
    nationalMaxDigits: 9,
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  { dial: "+20", iso2: "eg", name: "Egypt", nationalMaxDigits: 9 },
  { dial: "+27", iso2: "za", name: "South Africa", nationalMaxDigits: 9 },
  { dial: "+30", iso2: "gr", name: "Greece", nationalMaxDigits: 10 },
  {
    dial: "+31",
    iso2: "nl",
    name: "Netherlands",
    nationalMaxDigits: 9,
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  { dial: "+32", iso2: "be", name: "Belgium", nationalMaxDigits: 9 },
  {
    dial: "+33",
    iso2: "fr",
    name: "France",
    nationalMaxDigits: 9,
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  {
    dial: "+34",
    iso2: "es",
    name: "Spain",
    nationalMaxDigits: 9,
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{3})$/, fmt: "$1$2$3" },
  },
  { dial: "+36", iso2: "hu", name: "Hungary", nationalMaxDigits: 9 },
  { dial: "+39", iso2: "it", name: "Italy", nationalMaxDigits: 10 },
  { dial: "+40", iso2: "ro", name: "Romania", nationalMaxDigits: 9 },
  { dial: "+41", iso2: "ch", name: "Switzerland", nationalMaxDigits: 9 },
  { dial: "+43", iso2: "at", name: "Austria", nationalMaxDigits: 12 },
  { dial: "+44", iso2: "gb", name: "United Kingdom", nationalMaxDigits: 10 },
  { dial: "+45", iso2: "dk", name: "Denmark", nationalMaxDigits: 8 },
  { dial: "+46", iso2: "se", name: "Sweden", nationalMaxDigits: 9 },
  { dial: "+47", iso2: "no", name: "Norway", nationalMaxDigits: 8 },
  { dial: "+48", iso2: "pl", name: "Poland", nationalMaxDigits: 9 },
  { dial: "+49", iso2: "de", name: "Germany", nationalMaxDigits: 12 },
  { dial: "+51", iso2: "pe", name: "Peru", nationalMaxDigits: 9 },
  { dial: "+52", iso2: "mx", name: "Mexico", nationalMaxDigits: 10 },
  { dial: "+54", iso2: "ar", name: "Argentina", nationalMaxDigits: 10 },
  { dial: "+55", iso2: "br", name: "Brazil", nationalMaxDigits: 11 },
  { dial: "+56", iso2: "cl", name: "Chile", nationalMaxDigits: 9 },
  { dial: "+57", iso2: "co", name: "Colombia", nationalMaxDigits: 10 },
  { dial: "+60", iso2: "my", name: "Malaysia", nationalMaxDigits: 10 },
  { dial: "+61", iso2: "au", name: "Australia", nationalMaxDigits: 9 },
  { dial: "+62", iso2: "id", name: "Indonesia", nationalMaxDigits: 11 },
  { dial: "+63", iso2: "ph", name: "Philippines", nationalMaxDigits: 10 },
  { dial: "+64", iso2: "nz", name: "New Zealand", nationalMaxDigits: 9 },
  { dial: "+65", iso2: "sg", name: "Singapore", nationalMaxDigits: 8 },
  { dial: "+66", iso2: "th", name: "Thailand", nationalMaxDigits: 9 },
  { dial: "+81", iso2: "jp", name: "Japan", nationalMaxDigits: 10 },
  { dial: "+82", iso2: "kr", name: "South Korea", nationalMaxDigits: 10 },
  { dial: "+84", iso2: "vn", name: "Vietnam", nationalMaxDigits: 9 },
  { dial: "+86", iso2: "cn", name: "China", nationalMaxDigits: 11 },
  { dial: "+90", iso2: "tr", name: "Turkey", nationalMaxDigits: 10 },
  { dial: "+91", iso2: "in", name: "India", nationalMaxDigits: 10 },
  { dial: "+92", iso2: "pk", name: "Pakistan", nationalMaxDigits: 10 },
  { dial: "+93", iso2: "af", name: "Afghanistan", nationalMaxDigits: 9 },
  { dial: "+94", iso2: "lk", name: "Sri Lanka", nationalMaxDigits: 9 },
  { dial: "+7", iso2: "ru", name: "Russia", nationalMaxDigits: 10 },
  {
    dial: "+1",
    iso2: "us",
    name: "United States",
    nationalMaxDigits: 10,
    nationalFormat: { re: /^(\d{3})(\d{3})(\d{4})$/, fmt: "$1$2$3" },
  },
] as const;
